import {
  resolveDynamicFlag,
  useDynamicFlag,
} from "@/utils/featureFlagOverride";
import { createSiteAllowlistFlag } from "@/utils/siteAllowlistFlag";

// Gates support for more than two variations (N-way A/B). Global on/off via
// VITE_FF_ENABLE_MULTI_VARIATION (runtime-overridable kill switch), plus a
// per-site allowlist. Off caps the "add variant" tab at 2.
export const MULTI_VARIATION_FLAG_KEY = "VITE_FF_ENABLE_MULTI_VARIATION";

const multiVariationEnvValue =
  import.meta.env.VITE_FF_ENABLE_MULTI_VARIATION === "true";

const multiVariationSiteAllowlistCheck = createSiteAllowlistFlag(
  undefined,
  import.meta.env.VITE_FF_MULTI_VARIATION_SITES,
);

export const isMultiVariationEnabledForSite = (siteId: string | undefined) =>
  resolveDynamicFlag(MULTI_VARIATION_FLAG_KEY, multiVariationEnvValue) ||
  multiVariationSiteAllowlistCheck(siteId);

export const useIsMultiVariationEnabledForSite = (
  siteId: string | undefined,
) => {
  const globalOverride = useDynamicFlag(
    MULTI_VARIATION_FLAG_KEY,
    multiVariationEnvValue,
  );
  return globalOverride || multiVariationSiteAllowlistCheck(siteId);
};

export const BROADCAST_V2_MAINTENANCE_FLAG_KEY =
  "VITE_FF_BROADCAST_V2_MAINTENANCE";

const broadcastV2MaintenanceEnvValue =
  import.meta.env.VITE_FF_BROADCAST_V2_MAINTENANCE === "true";

export const isBroadcastV2UnderMaintenance = () =>
  resolveDynamicFlag(
    BROADCAST_V2_MAINTENANCE_FLAG_KEY,
    broadcastV2MaintenanceEnvValue,
  );

export const useIsBroadcastV2UnderMaintenance = () =>
  useDynamicFlag(
    BROADCAST_V2_MAINTENANCE_FLAG_KEY,
    broadcastV2MaintenanceEnvValue,
  );

export const SMART_SEND_FLAG_KEY = "VITE_FF_ENABLE_SMART_SEND";

const smartSendEnvValue = import.meta.env.VITE_FF_ENABLE_SMART_SEND === "true";

const smartSendSiteAllowlistCheck = createSiteAllowlistFlag(
  import.meta.env.VITE_FF_ENABLE_SMART_SEND,
  import.meta.env.VITE_FF_SMART_SEND_SITES,
);

export const isSmartSendEnabledForSite = (siteId: string | undefined) =>
  resolveDynamicFlag(SMART_SEND_FLAG_KEY, smartSendEnvValue) ||
  smartSendSiteAllowlistCheck(siteId);

export const useIsSmartSendEnabledForSite = (siteId: string | undefined) => {
  const globalOverride = useDynamicFlag(SMART_SEND_FLAG_KEY, smartSendEnvValue);
  return globalOverride || smartSendSiteAllowlistCheck(siteId);
};

// Gates the Override Frequency Caps toggle on email broadcasts. Global on/off
// via VITE_FF_ENABLE_EMAIL_FREQUENCY_CAP_OVERRIDE (runtime-overridable), plus a
// per-site allowlist. SMS broadcasts always show the toggle.
export const EMAIL_FREQUENCY_CAP_OVERRIDE_FLAG_KEY =
  "VITE_FF_ENABLE_EMAIL_FREQUENCY_CAP_OVERRIDE";

const emailFrequencyCapOverrideEnvValue =
  import.meta.env.VITE_FF_ENABLE_EMAIL_FREQUENCY_CAP_OVERRIDE === "true";

const emailFrequencyCapOverrideSiteAllowlistCheck = createSiteAllowlistFlag(
  import.meta.env.VITE_FF_ENABLE_EMAIL_FREQUENCY_CAP_OVERRIDE,
  import.meta.env.VITE_FF_EMAIL_FREQUENCY_CAP_OVERRIDE_SITES,
);

export const isEmailFrequencyCapOverrideEnabledForSite = (
  siteId: string | undefined,
) =>
  resolveDynamicFlag(
    EMAIL_FREQUENCY_CAP_OVERRIDE_FLAG_KEY,
    emailFrequencyCapOverrideEnvValue,
  ) || emailFrequencyCapOverrideSiteAllowlistCheck(siteId);

export const useIsEmailFrequencyCapOverrideEnabledForSite = (
  siteId: string | undefined,
) => {
  const globalOverride = useDynamicFlag(
    EMAIL_FREQUENCY_CAP_OVERRIDE_FLAG_KEY,
    emailFrequencyCapOverrideEnvValue,
  );
  return globalOverride || emailFrequencyCapOverrideSiteAllowlistCheck(siteId);
};
